/* eslint-disable @typescript-eslint/no-explicit-any */
import { DrawerAccordionContentItem } from "@/components/organisms/medical-section/medical-screen";
import { MagnoliaCategory, MagnoliaNode } from "@/types/magnolia-utils";

export function normaliseMagnoliaResponse(
  node?: MagnoliaNode,
  childNodes: string[] = [],
  result: Record<string, unknown> = {}
): any {
  if (!node) return null;
  const nodes = childNodes.length ? childNodes : node?.["@nodes"];
  nodes.forEach((n) => {
    const magnoliaNode = node[n] as MagnoliaNode;
    const updatedNodes = magnoliaNode["@nodes"];

    // Omit unused magnolia props
    const {
      "@id": _id,
      "@name": _name,
      "@nodeType": _nodeType,
      "@path": _path,

      "@nodes": _nodes,
      "mgnl:createdBy": _createdBy,
      "mgnl:lastModified": _lastModified,
      "mgnl:created": _created,
      ...others
    } = magnoliaNode;
    Object.entries(others).forEach(([key, value]) => {
      if (value && typeof value === "object") {
        const valueNode = value as MagnoliaNode;

        // Map magnolia categories to their displayName property
        if (valueNode["@nodeType"] === "mgnl:category") {
          others[key] = (value as MagnoliaCategory).displayName;
          return;
        }
      }

      others[key] = value;
    });

    result[n] = others;

    // Run on nested nodes
    normaliseMagnoliaResponse(
      magnoliaNode,
      updatedNodes,
      result[n] as Record<string, unknown>
    );
  });

  return result;
}

function processNestedObject(nestedObj: any) {
  const resultArray: any = [];
  const nestedKeys = Object.keys(nestedObj);

  nestedKeys.forEach((key) => {
    const item = nestedObj[key];
    const newItem: any = {};

    Object.keys(item).forEach((itemKey) => {
      if (itemKey === "mgnl:template") {
        newItem.template = item[itemKey];
      } else {
        newItem[itemKey] = item[itemKey];
      }
    });

    if (typeof item === "object" && !Array.isArray(item) && item !== null) {
      resultArray.push(newItem);
    }
  });

  return resultArray;
}

export function restructureObject(obj: any) {
  const newObj: any = {};
  const keys = obj ? Object.keys(obj) : [];

  if (keys.length > 0) {
    const parentKey = keys[0];
    const parentObj = obj[parentKey];

    newObj.template = parentObj["mgnl:template"];
    newObj.title = parentObj?.title;
    newObj.optimizelyTestEnabledTitle = parentObj?.optimizelyTestEnabledTitle;
    newObj.child = [];

    Object.keys(parentObj).forEach((key) => {
      if (
        key !== "mgnl:template" &&
        key !== "title" &&
        key !== "optimizelyTestEnabledTitle"
      ) {
        const newChild: any = {};
        newChild[key] = processNestedObject(parentObj[key]);
        newObj.child.push(newChild);
      }
    });
  }

  return newObj;
}

export function extractMedicalDrawerAccordionDetails(
  medicalDrawerObject: any
): DrawerAccordionContentItem[] {
  const screeningArea = medicalDrawerObject?.medicalScreeningArea?.["0"];
  const detailsNode = screeningArea?.medicalDrawerAccordionDetails;

  if (!detailsNode?.["@nodes"]) {
    return [];
  }
  const nodeKeys = detailsNode["@nodes"] as string[];

  const detailsArray: DrawerAccordionContentItem[] = nodeKeys.map((key) => {
    const node = detailsNode[key];
    const { "@id": id } = node; // extract @id and rename to id
    return {
      id,
      title: node.title,
      content: node.content,
    };
  });

  return detailsArray;
}

export interface VeriskErrorModalContent {
  title: string;
  description: string;
  ctaLabel: string;
}

export function extractVeriskErrorModalContent(
  medicalScreenObject: any
): VeriskErrorModalContent {
  const screeningArea = medicalScreenObject?.medicalScreeningArea?.["0"];

  return {
    title: screeningArea?.veriskUnavailableModalTitle,
    description: screeningArea?.veriskUnavailableModalDescription,
    ctaLabel: screeningArea?.veriskUnavailableModalCtaLabel,
  };
}
